// The `GI.*` LAMBDA set for the linked `.xlsx` export — ported from the
// giraffe-excel-poc prototype's hand-written Name Manager formulas
// (docs/EXCEL_SETUP.md in that repo), adapted to this project's simpler
// needs and NOT independently verified against real Excel (the PoC's own
// author flags its whole LAMBDA set the same way: "none of this has been
// tested against real Excel yet"). Treat this as a well-reasoned starting
// point for the live verification step the brief's own checklist calls for
// (open the exported file in real Excel, Refresh All, confirm no #NAME?/#REF!),
// not as a finished, confirmed feature.
//
// Two simplifications versus the PoC's own set, both deliberate:
//
// 1. No expression evaluator. The PoC needed one (16 interlocking private
//    LAMBDAs) because it had no formula engine of its own and had to make
//    Excel evaluate arbitrary `area*hardCost` text. gTables already evaluates
//    every expression itself — a custom-calc column on the Areas sheet
//    materialises one BEFORE export (data/featureTable.ts's tier-d columns)
//    — so the exported `GI.SUM`/`GI.WHERE` here only ever do a plain
//    field-name lookup, exactly decision #4 of the brief this was built
//    from: "the exported LAMBDA never evaluates an expression string — it
//    just does a column lookup by header name."
//
// 2. A RANGE parameter instead of a named Excel Table. The PoC's LAMBDAs
//    hardcode `tblFeatures` by name; these take the table (header row +
//    data rows, as one range) as their first argument instead — e.g.
//    `GI.SUM(Features!$A$1:$P$50, "grossArea")` — so the SAME LAMBDA applies
//    uniformly to any exported sheet by just passing a different range
//    (there is now only ever the one — Features — since its own custom-calc
//    columns replaced the separate Measures sheet this comment used to name),
//    with no Excel Table XML part needed at all. Simpler to generate
//    correctly than authoring `xl/tables/*.xml`,
//    at the cost of a workbook that does not auto-grow a table on paste —
//    acceptable for a sheet nobody pastes into (every row here comes from
//    gTables' own export, not from typing).
//
// Every formula assumes Excel 365's dynamic arrays (LET, LAMBDA, SEQUENCE,
// FILTER, UNIQUE, TEXTSPLIT, TEXTJOIN) — the same assumption the PoC made.

export interface GiLambdaDef {
  name: string;
  formula: string;
}

/**
 * One row — `field op value`, e.g. `usage=Residential` or `levels>4` — quoted
 * the same way `GI.WHERE` quotes it when the value needs it (a comma or a
 * quote character). Mirrors `quoteFilterValue` in `engine/fns/gi.ts`.
 */
const CLAUSETEXT = `LAMBDA(field,value,
  LET(
    hasOp,OR(RIGHT(field,1)="=",RIGHT(field,1)=">",RIGHT(field,1)="<",RIGHT(field,1)="~",RIGHT(field,2)="!=",RIGHT(field,2)=">=",RIGHT(field,2)="<="),
    clauseField,IF(hasOp,field,field&"="),
    needsQuote,OR(ISNUMBER(SEARCH(",",value)),ISNUMBER(SEARCH(CHAR(34),value)),ISNUMBER(SEARCH("'",value))),
    clauseValue,IF(needsQuote,CHAR(34)&value&CHAR(34),value),
    clauseField&clauseValue
  )
)`;

/** A boolean mask (as 1/0) over `range`'s data rows for one `field op value` clause; blank clause = no filter. */
const CLAUSEMASK = `LAMBDA(range,clause,
  IF(clause="",
    1,
    LET(
      posBang,IFERROR(FIND("!",clause),999),
      posGt,IFERROR(FIND(">",clause),999),
      posLt,IFERROR(FIND("<",clause),999),
      posEq,IFERROR(FIND("=",clause),999),
      posTilde,IFERROR(FIND("~",clause),999),
      opPos,MIN(posBang,posGt,posLt,posEq,posTilde),
      twoChar,MID(clause,opPos,2),
      opLen,IF(OR(twoChar="!=",twoChar=">=",twoChar="<="),2,1),
      op,MID(clause,opPos,opLen),
      field,TRIM(LEFT(clause,opPos-1)),
      value,TRIM(MID(clause,opPos+opLen,LEN(clause)-opPos-opLen+1)),
      headerRow,INDEX(range,1,0),
      colNum,MATCH(field,headerRow,0),
      dataRows,ROWS(range)-1,
      dataCol,INDEX(range,SEQUENCE(dataRows,1,2,1),colNum),
      numericValue,IFERROR(VALUE(value),""),
      cmpValue,IF(numericValue="",value,numericValue),
      IF(op="!=",--(dataCol<>cmpValue),
      IF(op=">=",--(dataCol>=cmpValue),
      IF(op="<=",--(dataCol<=cmpValue),
      IF(op=">",--(dataCol>cmpValue),
      IF(op="<",--(dataCol<cmpValue),
      IF(op="~",--ISNUMBER(SEARCH(value,dataCol)),
        --(dataCol=cmpValue)
      ))))))
    )
  )
)`;

/** Every comma-separated clause in `filter`, ANDed — up to 5, a practical ceiling for a fixed-arity LAMBDA. */
const MATCH_FN = `LAMBDA(range,filter,
  LET(
    rawClauses,TEXTSPLIT(filter,","),
    n,COLUMNS(rawClauses),
    clause1,IF(n>=1,TRIM(INDEX(rawClauses,1,1)),""),
    clause2,IF(n>=2,TRIM(INDEX(rawClauses,1,2)),""),
    clause3,IF(n>=3,TRIM(INDEX(rawClauses,1,3)),""),
    clause4,IF(n>=4,TRIM(INDEX(rawClauses,1,4)),""),
    clause5,IF(n>=5,TRIM(INDEX(rawClauses,1,5)),""),
    GI._CLAUSEMASK(range,clause1)*GI._CLAUSEMASK(range,clause2)*GI._CLAUSEMASK(range,clause3)*GI._CLAUSEMASK(range,clause4)*GI._CLAUSEMASK(range,clause5)
  )
)`;

/** `field`'s column over `range`'s data rows, blanked wherever `filter` does not match — what every aggregate below applies a native Excel function to. */
const ROWVALUES = `LAMBDA(range,field,filter,
  LET(
    mask,GI._MATCH(range,filter),
    headerRow,INDEX(range,1,0),
    colNum,MATCH(field,headerRow,0),
    dataRows,ROWS(range)-1,
    dataCol,INDEX(range,SEQUENCE(dataRows,1,2,1),colNum),
    IF(mask=1,dataCol,"")
  )
)`;

const aggregate = (fn: string): string => `LAMBDA(range,field,[filter],
  ${fn}(GI._ROWVALUES(range,field,IF(ISOMITTED(filter),"",filter)))
)`;

export const GI_LAMBDAS: GiLambdaDef[] = [
  { name: 'GI._CLAUSETEXT', formula: CLAUSETEXT },
  { name: 'GI._CLAUSEMASK', formula: CLAUSEMASK },
  { name: 'GI._MATCH', formula: MATCH_FN },
  { name: 'GI._ROWVALUES', formula: ROWVALUES },
  {
    name: 'GI.WHERE',
    formula: `LAMBDA(field1,value1,[field2],[value2],[field3],[value3],[field4],[value4],[field5],[value5],
  LET(
    c1,GI._CLAUSETEXT(field1,value1),
    c2,IF(ISOMITTED(field2),"",GI._CLAUSETEXT(field2,value2)),
    c3,IF(ISOMITTED(field3),"",GI._CLAUSETEXT(field3,value3)),
    c4,IF(ISOMITTED(field4),"",GI._CLAUSETEXT(field4,value4)),
    c5,IF(ISOMITTED(field5),"",GI._CLAUSETEXT(field5,value5)),
    TEXTJOIN(", ",TRUE,c1,c2,c3,c4,c5)
  )
)`,
  },
  {
    name: 'GI.ALL',
    formula: `LAMBDA(filter1,[filter2],[filter3],[filter4],[filter5],
  LET(
    f2,IF(ISOMITTED(filter2),"",filter2),
    f3,IF(ISOMITTED(filter3),"",filter3),
    f4,IF(ISOMITTED(filter4),"",filter4),
    f5,IF(ISOMITTED(filter5),"",filter5),
    TEXTJOIN(", ",TRUE,filter1,f2,f3,f4,f5)
  )
)`,
  },
  { name: 'GI.SUM', formula: aggregate('SUM') },
  { name: 'GI.COUNT', formula: aggregate('COUNT') },
  { name: 'GI.AVERAGE', formula: aggregate('AVERAGE') },
  { name: 'GI.MIN', formula: aggregate('MIN') },
  { name: 'GI.MAX', formula: aggregate('MAX') },
  {
    name: 'GI.VALUE',
    formula: `LAMBDA(range,field,filter,
  LET(
    mask,GI._MATCH(range,filter),
    headerRow,INDEX(range,1,0),
    colNum,MATCH(field,headerRow,0),
    dataRows,ROWS(range)-1,
    dataCol,INDEX(range,SEQUENCE(dataRows,1,2,1),colNum),
    IFERROR(INDEX(FILTER(dataCol,mask=1),1),"n/a")
  )
)`,
  },
  {
    name: 'GI.WEIGHTEDAVG',
    formula: `LAMBDA(range,field,[filter],
  LET(
    f,IF(ISOMITTED(filter),"",filter),
    mask,GI._MATCH(range,f),
    headerRow,INDEX(range,1,0),
    dataRows,ROWS(range)-1,
    valueCol,INDEX(range,SEQUENCE(dataRows,1,2,1),MATCH(field,headerRow,0)),
    grossAreaCol,IFERROR(INDEX(range,SEQUENCE(dataRows,1,2,1),MATCH("grossArea",headerRow,0)),0),
    areaCol,IFERROR(INDEX(range,SEQUENCE(dataRows,1,2,1),MATCH("area",headerRow,0)),0),
    grossAreaWeightSum,SUM(mask*grossAreaCol),
    weightCol,IF(grossAreaWeightSum>0,grossAreaCol,areaCol),
    weightSum,SUM(mask*weightCol),
    matchCount,SUM(mask),
    IF(matchCount=0,"n/a",IF(weightSum=0,SUM(mask*valueCol)/matchCount,SUMPRODUCT(mask,weightCol,valueCol)/weightSum))
  )
)`,
  },
  {
    name: 'GI.LIST',
    formula: `LAMBDA(range,field,index,[filter],[sort],
  LET(
    f,IF(ISOMITTED(filter),"",filter),
    desc,IF(ISOMITTED(sort),FALSE,UPPER(LEFT(sort,4))="DESC"),
    mask,GI._MATCH(range,f),
    headerRow,INDEX(range,1,0),
    dataRows,ROWS(range)-1,
    dataCol,INDEX(range,SEQUENCE(dataRows,1,2,1),MATCH(field,headerRow,0)),
    candidate,IF(mask=1,dataCol,""),
    hasAny,SUM(--(candidate<>"")),
    sorted,IF(hasAny=0,"",SORT(UNIQUE(FILTER(candidate,candidate<>"")),1,IF(desc,-1,1))),
    IF(hasAny=0,"",IFERROR(INDEX(sorted,index),""))
  )
)`,
  },
];
